<template>
  <div class="auth-page">

    <!-- Left branding panel -->
    <div class="auth-brand">
      <div class="auth-brand__inner">
        <router-link to="/" class="auth-logo">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else class="auth-logo__text">Structure<span>city</span></span>
        </router-link>
        <div class="auth-brand__body">
          <h1>Account Verification</h1>
          <p>One last step — verifying your email confirms your identity and activates your Structurecity account.</p>
          <div class="auth-steps">
            <div class="auth-step auth-step--done">
              <span class="auth-step__icon">✓</span>
              <span>Created your account</span>
            </div>
            <div class="auth-step auth-step--done">
              <span class="auth-step__icon">✓</span>
              <span>Received verification email</span>
            </div>
            <div class="auth-step" :class="{ 'auth-step--done': reply === 1, 'auth-step--fail': reply === 2 }">
              <span class="auth-step__icon">{{ reply === 1 ? '✓' : reply === 2 ? '✕' : '3' }}</span>
              <span>Verify your email address</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right result panel -->
    <div class="auth-panel">
      <div class="auth-panel__inner">

        <!-- Mobile logo -->
        <router-link to="/" class="auth-logo auth-logo--mobile">
          <img v-if="company && company.logo" :src="imgUrl + 'logo/' + company.logo" alt="Structurecity" />
          <span v-else class="auth-logo__text">Structure<span>city</span></span>
        </router-link>

        <!-- Awaiting -->
        <div v-if="reply === 0" class="verify-state verify-state--loading">
          <div class="verify-spinner"></div>
          <h2>Verifying your account…</h2>
          <p>Please wait while we confirm your email address.</p>
        </div>

        <!-- Success -->
        <div v-else-if="reply === 1" class="verify-state verify-state--success">
          <div class="verify-icon verify-icon--success">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2>Email verified!</h2>
          <p>Your account is now active. You can sign in and start connecting with professionals across Africa.</p>
          <router-link to="/sign-in" class="auth-btn">Sign in to your account</router-link>
          <p class="auth-switch"><router-link to="/" class="auth-link">← Back to home</router-link></p>
        </div>

        <!-- Failure -->
        <div v-else class="verify-state verify-state--error">
          <div class="verify-icon verify-icon--error">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </div>
          <h2>Verification failed</h2>
          <p>We couldn't verify your account. The link may have expired or already been used.</p>
          <div class="verify-actions">
            <router-link to="/sign-in" class="auth-btn">Go to sign in</router-link>
            <router-link to="/" class="auth-btn-ghost">Back to home</router-link>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'VerifyAccount',

  data() {
    return {
      imgUrl: process.env.VUE_APP_IMAGE_URL || '',
      company: null,
      reply: 0,
    };
  },

  watch: {
    $route: { immediate: true, handler(to) { document.title = to.meta && to.meta.title ? to.meta.title : 'Verify Account — Structurecity'; } },
  },

  created() {
    this.fetchCompany();
    this.verifyAccount();
  },

  methods: {
    verifyAccount() {
      axios.get(process.env.VUE_APP_API_URL + 'auth/activate/' + this.$route.params.id)
        .then(res => {
          if (res.status === 200 || res.status === 201) {
            this.reply = 1;
          } else {
            this.reply = 2;
          }
        })
        .catch(() => { this.reply = 2; });
    },

    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res && res.data) this.company = res.data; })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.auth-page {
  display: grid; grid-template-columns: 1fr 1fr;
  min-height: 100vh;
}

/* ── Left branding panel ── */
.auth-brand {
  background: linear-gradient(145deg, #0a1628 0%, #1e3a5f 50%, #1a4a7a 100%);
  padding: 3rem; display: flex; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
}
.auth-brand::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.05) 1px, transparent 1px);
  background-size: 36px 36px;
}
.auth-brand__inner {
  position: relative; z-index: 1; max-width: 420px; width: 100%;
  display: flex; flex-direction: column; gap: 2.5rem;
}

.auth-logo { display: flex; align-items: center; text-decoration: none; }
.auth-logo img { height: 42px; object-fit: contain; }
.auth-logo__text { font-size: 1.75rem; font-weight: 800; color: #fff; letter-spacing: -.03em; }
.auth-logo__text span { color: #f59e0b; }
.auth-logo--mobile { display: none; }

.auth-brand__body { display: flex; flex-direction: column; gap: 1.25rem; }
.auth-brand__body h1 {
  font-size: clamp(1.5rem, 2.5vw, 2rem); font-weight: 800; color: #fff;
  line-height: 1.2; letter-spacing: -.03em; margin: 0;
}
.auth-brand__body p { color: rgba(255,255,255,.65); font-size: 1rem; line-height: 1.65; margin: 0; }

.auth-steps { display: flex; flex-direction: column; gap: 1rem; margin-top: .5rem; }
.auth-step {
  display: flex; align-items: center; gap: 1rem;
  color: rgba(255,255,255,.55); font-size: .9375rem; font-weight: 500;
}
.auth-step__icon {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.2);
  color: rgba(255,255,255,.55); font-weight: 700; font-size: .875rem;
  display: flex; align-items: center; justify-content: center;
}
.auth-step--done { color: rgba(255,255,255,.9); }
.auth-step--done .auth-step__icon {
  background: rgba(34,197,94,.2); border-color: rgba(34,197,94,.5); color: #4ade80;
}
.auth-step--fail .auth-step__icon {
  background: rgba(239,68,68,.2); border-color: rgba(239,68,68,.5); color: #f87171;
}

/* ── Right panel ── */
.auth-panel {
  background: #fff; display: flex; align-items: center; justify-content: center;
  padding: 3rem 2rem;
}
.auth-panel__inner {
  width: 100%; max-width: 420px; display: flex; flex-direction: column; gap: 1.5rem;
}

/* ── Verify states ── */
.verify-state {
  display: flex; flex-direction: column; align-items: center; text-align: center;
  gap: 1.25rem;
}

.verify-spinner {
  width: 56px; height: 56px; border-radius: 50%;
  border: 3px solid #e2e8f0; border-top-color: #1e3a5f;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.verify-icon {
  width: 72px; height: 72px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.verify-icon--success { background: #f0fdf4; color: #16a34a; }
.verify-icon--error   { background: #fef2f2; color: #dc2626; }

.verify-state h2 {
  font-size: 1.875rem; font-weight: 800; color: #0f172a;
  margin: 0; letter-spacing: -.03em;
}
.verify-state p { color: #64748b; font-size: 1rem; line-height: 1.65; margin: 0; max-width: 340px; }

.verify-actions { display: flex; flex-direction: column; gap: .75rem; width: 100%; }

.auth-btn {
  width: 100%; padding: .8125rem 1.5rem;
  background: #1e3a5f; color: #fff; border: none; border-radius: 10px;
  font-family: inherit; font-size: 1rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: .5rem;
  text-decoration: none;
  transition: background .15s, box-shadow .15s;
}
.auth-btn:hover { background: #2a4f7c; box-shadow: 0 4px 16px rgba(30,58,95,.3); color: #fff; text-decoration: none; }

.auth-btn-ghost {
  width: 100%; padding: .8125rem 1.5rem;
  background: #f1f5f9; color: #475569; border: none; border-radius: 10px;
  font-family: inherit; font-size: 1rem; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  text-decoration: none;
  transition: background .15s;
}
.auth-btn-ghost:hover { background: #e2e8f0; color: #334155; text-decoration: none; }

.auth-switch { text-align: center; margin: 0; }
.auth-link { color: #1e3a5f; text-decoration: none; font-size: .875rem; font-weight: 500; }
.auth-link:hover { text-decoration: underline; }

/* ── Responsive ── */
@media (max-width: 900px) {
  .auth-page { grid-template-columns: 1fr; }
  .auth-brand { display: none; }
  .auth-logo--mobile { display: flex; }
  .auth-panel { padding: 2.5rem 1.5rem; min-height: 100vh; }
}
</style>
